<template>
  <div id="pdf" ref="pdf" class="report">
    <CoverPage v-if="study" class="page" :study="study" :export="studyExport" :filters="filters" :name="name" :logo="logo" />

    <template v-if="loaded && studyExport.type !== ExportType.Short">
      <IndexPage v-if="loaded" class="page" :study="study" :export="studyExport" :filters="filters" :index="index.slice(0, 2)" />
      <template v-if="loaded">
        <IndexPage
          v-for="(size, index) in tableOfContentEnumerable"
          :key="index"
          class="page"
          :study="study"
          :export="studyExport"
          :filters="filters"
          :index="indexSites.slice(size[0], size[1])"
        />
      </template>
      <InterlayerPage v-if="loaded" class="page" :title="$t('report.exportIntroduction')" :transports="[]" :comment="''" :filters="filters" />

      <IntroductionConfigPage v-if="loaded" :study="study" :export="studyExport" :filters="filters" :filterCollaborators="filterCollaborators" />
      <IntroductionExplanationPage v-if="loaded" :study="study" :export="studyExport" :filters="filters" />
      <IntroductionMapPage v-if="loaded" :study="study" :export="studyExport" :filters="filters" />
      <IntroductionCollaboratorPage
        v-if="loaded && studyExport"
        :study="study"
        :export="studyExport"
        :filters="filters"
        :filterCollaborators="filterCollaborators"
      />
      <IntroductionProvenancePage
        v-if="loaded && studyExport"
        :study="study"
        :export="studyExport"
        :filters="filters"
        :filterCollaborators="filterCollaborators"
      />

      <Analysis v-if="loaded && (indexPages || oldIndexPages)" :filters="filters" :name="name" />

      <CommentPage v-if="globalComment || !printMode" :comment="globalComment" class="page" />
    </template>

    <template v-else>
      <IntroductionMethodologyAndMapsPage v-if="loaded" :study="study" :export="studyExport" />

      <ShortAnalysisPage v-if="loaded" />
    </template>

    <EndPage v-if="study" class="page" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import FilterSpecs from '@/components/Export/filterSpecs';
import CoverPage from '@/components/Report/CoverPage';
import IndexPage from '@/components/Report/IndexPage';
import InterlayerPage from '@/components/Report/InterlayerPage';
import IntroductionMapPage from '@/components/Report/IntroductionMapPage';
import IntroductionCollaboratorPage from '@/components/Report/IntroductionCollaboratorPage';
import IntroductionProvenancePage from '@/components/Report/IntroductionProvenancePage';
import IntroductionConfigPage from '@/components/Report/IntroductionConfigPage';
import IntroductionExplanationPage from '@/components/Report/IntroductionExplanationPage';
import Analysis from '@/components/Export/Analysis/';
import CommentPage from '@/components/Report/CommentPage';
import EndPage from '@/components/Report/EndPage';
import ExportType from '@/pure/ExportType';
import IntroductionMethodologyAndMapsPage from '@/components/Report/ShortExport/IntroductionMethodologyAndMapsPage';
import ShortAnalysisPage from '@/components/Report/ShortExport/ShortAnalysisPage';

export default {
  components: {
    ShortAnalysisPage,
    IntroductionMethodologyAndMapsPage,
    CommentPage,
    CoverPage,
    EndPage,
    IndexPage,
    InterlayerPage,
    IntroductionMapPage,
    IntroductionCollaboratorPage,
    IntroductionProvenancePage,
    IntroductionConfigPage,
    IntroductionExplanationPage,
    Analysis,
  },

  data() {
    return {
      name: '',
      studyExport: null,
      filterCollaborators: [],
      printMode: false,
      oldIndexPages: 0,
    };
  },

  computed: {
    ...mapGetters(['currentExportLayout', 'tableOfContent', 'study', 'indexPages', 'exportLogo', 'globalComment']),

    ...mapGetters({
      actualSites: 'exportActualSites',
      logo: 'exportLogo',
    }),

    ExportType() {
      return ExportType;
    },

    filters() {
      if (!this.studyExport) {
        return null;
      }

      let filters = [];
      if (this.studyExport && this.studyExport.filters.favorite_transports.filter(o => !o.enabled).length > 0) {
        filters.push(this.$t('filters.transportModes'));
      }
      if (this.studyExport && this.studyExport.filters.contract_types.filter(o => !o.enabled).length > 0) {
        filters.push(this.$t('filters.contract_types'));
      }
      if (this.studyExport && this.studyExport.filters.services.filter(o => !o.enabled).length > 0) {
        filters.push(this.$t('filters.services'));
      }
      if (this.studyExport && this.studyExport.filters.seniorities.filter(o => !o.enabled).length > 0) {
        filters.push(this.$t('filters.seniorities'));
      }
      if (this.studyExport && this.studyExport.filters.filter1.filter(o => !o.enabled).length > 0) {
        filters.push(this.studyExport.filters.filter1_name);
      }
      if (this.studyExport && this.studyExport.filters.filter2.filter(o => !o.enabled).length > 0) {
        filters.push(this.studyExport.filters.filter2_name);
      }

      return filters.toString().replace(/,/g, ', ');
    },

    tableOfContentEnumerable() {
      const { actualSites } = this;
      const size = actualSites[0] ? (actualSites[0].transports.length > 2 ? 1 : 2) : 2;
      const count = actualSites.length > 1 ? Math.ceil(actualSites.length / size + 0.5) : 1;
      const enumerable = [];
      for (let i = 0; i < count; i++) {
        enumerable.push([i * size, i * size + size]);
      }

      return this.studyExport && this.studyExport.type === ExportType.Simplified ? [0] : enumerable;
    },

    introduction() {
      let count = this.indexPages + 1;

      return {
        id: 0,
        name: this.$t('report.exportIntroduction'),
        page: count++,
        lines: [
          {
            id: 0,
            name: this.$t('report.studyParameters'),
            page: count++,
          },
          {
            id: 1,
            name: this.$t('report.calculSource'),
            page: count++,
          },
          {
            id: 2,
            name: this.$t('report.currentSitesTargetSites'),
            page: count++,
          },
          {
            id: 3,
            name: this.$t('report.collaboratorSource'),
            page: count++,
          },
          {
            id: 4,
            name: this.$t('report.collaboratorSourceByRegion'),
            page: count++,
          },
        ],
      };
    },

    index() {
      return [this.introduction];
    },

    indexSites() {
      return this.tableOfContent;
    },

    loaded() {
      return !!this.studyExport && !!this.study;
    },
  },

  async created() {
    if (this.$route.query.apikey) {
      this.printMode = true;
      await this.$store.dispatch('forceApiKey', this.$route.query.apikey);
    }

    if (this.$route.query.language) {
      const lang = this.$route.query.language;
      this.$i18n.locale = lang;
      await this.$store.dispatch('changeLanguage', lang);
    }

    if (this.$route.params.exportId) {
      const studyExport = await this.$store.dispatch('getExportLayout', { id: this.$route.params.exportId });
      await this.$store.dispatch('getStudyResult', { id: studyExport.study_public_id });
      await this.$store.dispatch('getExport', { id: this.$route.params.exportId });

      this.studyExport = studyExport;
    } else {
      this.studyExport = this.currentExportLayout;
    }

    const data = await this.$store.dispatch('getStudyLightview', { id: this.studyExport.study_public_id });

    this.name = data.name;
    this.filterCollaborators = this.getFilterCollaborators();

    setTimeout(() => {
      console.log('Ready to print');
    }, 10000);
  },

  mounted() {
    this.oldIndexPages = this.indexPages;
    this.$store.dispatch('resetPage');
  },

  methods: {
    getFilterCollaborators() {
      let exportFilters = {};

      for (const filter in this.studyExport.filters) {
        if (Array.isArray(this.studyExport.filters[filter])) {
          for (const i in this.studyExport.filters[filter]) {
            if (!this.studyExport.filters[filter][i].enabled) {
              let value = this.studyExport.filters[filter][i].value.toLowerCase();

              if (filter === 'favorite_transports') {
                switch (value) {
                  case 'bike':
                    value = 'bicycle';
                    break;
                  case 'car':
                    value = 'private';
                    break;
                  case 'transport':
                    value = 'public';
                    break;
                  case 'none':
                    value = 'none';
                    break;
                  default:
                    break;
                }
              }

              if (Array.isArray(exportFilters[FilterSpecs[filter].key])) {
                exportFilters[FilterSpecs[filter].key].push(value);
              } else {
                exportFilters[FilterSpecs[filter].key] = [value];
              }
            }
          }
        }
      }

      if (!this.study) return [];
      let collaborators = this.study.collaborators.filter(item => item.address != null && item.sites && item.sites.length > 0);

      for (const filter in exportFilters) {
        if (exportFilters[filter].length > 0) {
          collaborators = collaborators.filter(person => {
            const filters = exportFilters[filter].map(e => {
              return e === '' ? 'Non renseigné'.toLowerCase() : e.toLowerCase();
            });

            return !filters.includes(person.details[filter].toLowerCase());
          });
        }
      }

      return collaborators;
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';

@page {
  size: A4 landscape;
  margin: 0;
}

.report {
  padding-top: 192px;
  overflow-x: hidden;
  overflow-y: auto;
  background: colors.$dark-color;

  :deep(.page) {
    width: 297mm;
    height: 209.5mm;
    background-color: colors.$white-color;
    border-radius: 4px;
    overflow: hidden;
    box-shadow:
      0 4px 24px 0 rgba(0, 0, 0, 0.1),
      0 4px 8px -4px rgba(0, 0, 0, 0.4);

    margin: 64px auto;
  }

  :deep(.page-number) {
    opacity: 0;
  }
}

@media print {
  .report {
    padding-top: 0;
    background: colors.$white-color;

    :deep(.page) {
      transform: scale(1);
      margin: 0;
      box-shadow: none;
      border-radius: 0;
    }

    :deep(.page-number) {
      opacity: 1;
    }
  }
}
</style>
